import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// ** rxjs
import { Observable, forkJoin, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { NewsService } from '@app/core/services/news.service';

import { Paginator } from 'primeng/paginator';

@Component({
    selector: 'app-news-center',
    templateUrl: './news-center.component.html',
    styleUrls: ['./news-center.component.scss'],
})
export class NewsCenterComponent implements OnInit {
    @ViewChild('paginator', { static: false }) paginator: Paginator;

    isBrowser: boolean;
    newsType = 2; // 預設為新聞
    news = []; // 當前新聞類型所有資料
    newsByPage = []; // 當前頁數之新聞

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId,
        private cdf: ChangeDetectorRef,
        private newsService: NewsService,
        private layoutService: LayoutService,
        public router: Router,
        private route: ActivatedRoute,
    ) {
        /** ssr/ssg 要先確保是不是在瀏覽器運行，避免 ref err */
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit(): void {
        const canonical = this.document.querySelector(
            'link[rel="canonical"]',
        ) as HTMLAnchorElement | null;

        if (canonical) {
            canonical.href = 'https://www.kunyoutech.com.tw/newsCenter';
        }

        this.route.queryParams
            .pipe(
                tap(() => this.layoutService.setPageLoading(true)),
                tap(
                    params => (this.newsType = Number(params['newsType'] ?? 2)),
                ),
                tap(() =>
                    this.newsType === 3
                        ? this.getElectronicNews()
                        : this.getNews(),
                ),
                tap(() => {
                    this.getDataByPage({ page: 0 }, true);
                    this.cdf.detectChanges();
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    changeNewsType(type, event) {
        this.paginator.changePageToFirst(event);
        switch (type) {
            case 1:
                this.getLatestNews();
                break;
            case 2:
                this.getNews();
                break;
            case 3:
                this.getElectronicNews();
                break;
        }
        this.newsType = type;
    }

    getDataByPage(event, isFirstCall = false) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(
                    () =>
                        (this.newsByPage = this.news.slice(
                            event.page * 8,
                            (event.page + 1) * 8,
                        )),
                ),
                tap(() => {
                    if (!this.isBrowser || isFirstCall) return;
                    let element = document.getElementById('copInfoArea');
                    let targetPosition = element.offsetTop - 65;
                    setTimeout(
                        () =>
                            window.scrollTo({
                                top: targetPosition,
                                behavior: 'smooth',
                            }),
                        250,
                    );
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    getLatestNews() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() =>
                    forkJoin([this.getNews$(), this.getElectronicNews$()]),
                ),
                tap(([news, eNews]) => {
                    this.news = [...news, ...eNews];
                    this.news.sort((a, b) => {
                        const dateA = new Date(a.Date);
                        const dateB = new Date(b.Date);
                        return dateB.getTime() - dateA.getTime();
                    });
                    this.getDataByPage({ page: 0 }, true);
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    getNews() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getNews$()),
                tap(res => {
                    this.news = res;
                    this.getDataByPage({ page: 0 });
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    getElectronicNews() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getElectronicNews$()),
                tap(res => {
                    this.news = res;
                    this.getDataByPage({ page: 0 });
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    goViewNews(prefix, fileTitle) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() =>
                    setTimeout(() =>
                        this.router.navigateByUrl(
                            `newsCenter/${prefix}/${fileTitle}`,
                        ),
                    ),
                ),
            )
            .subscribe();
    }

    /* -------------------------------------------------------------------------- */
    /*                                     api                                    */
    /* -------------------------------------------------------------------------- */

    getNews$(): Observable<any> {
        return this.newsService.getNews().pipe();
    }

    getElectronicNews$(): Observable<any> {
        return this.newsService.getElectronicNews().pipe();
    }
}
